import { h, Host } from "@stencil/core";
import { clamp, isValidNumber, toMaxPrecision } from "../../utils/number-utils/numbers";
import { Direction } from "../input-field-number/interface";
import { isKeyboardClickKey } from "./utils";
import { ButtonSize } from "../button/button.enum";
export class Stepper {
    constructor() {
        this.name = "";
        this.placeholder = undefined;
        this.label = undefined;
        this.defaultValue = undefined;
        this.value = undefined;
        this.disabled = false;
        this.required = false;
        this.hintText = undefined;
        this.error = false;
        this.errorMsg = undefined;
        this.locale = undefined;
        this.min = undefined;
        this.max = undefined;
        this.step = 1;
        this.precision = 0;
        this.unit = undefined;
        this.formatter = undefined;
        this.parser = undefined;
    }
    inputFieldNumberElement;
    isStepUpDisabled = false;
    isStepDownDisabled = false;
    timerId;
    domNode;
    /** Emitted when the value has changed. */
    uxChange;
    handleDisabledChanged() {
        this.checkDisabled();
        this.clearStepTimer();
    }
    /**
     * Performs a validity check on the input and sets `invalid` accordingly.
     */
    async validate() {
        this.inputFieldNumberElement.validate();
        this.error = this.inputFieldNumberElement.error;
    }
    componentWillRender() {
        if (this.value === undefined) {
            // @ts-expect-error Check for typing
            this.value = this.defaultValue;
        }
        this.checkDisabled();
    }
    disconnectedCallback() {
        this.clearStepTimer();
    }
    checkDisabled() {
        this.isStepUpDisabled = this.disabled || (this.max == null ? false : this.value >= this.max);
        this.isStepDownDisabled = this.disabled || (this.min == null ? false : this.value <= this.min);
    }
    setStepTimer(direction) {
        const loopStep = () => {
            this.checkDisabled();
            if (this.isStepDownDisabled || this.isStepUpDisabled) {
                return;
            }
            this.handleButtonClick(direction);
            this.timerId = setTimeout(loopStep, 100);
        };
        this.timerId = setTimeout(loopStep, 600);
    }
    clearStepTimer() {
        clearTimeout(this.timerId);
    }
    getStepDelta(direction) {
        return direction * this.step;
    }
    incrementValue(delta) {
        const currentValue = isValidNumber(this.value) ? this.value : isValidNumber(this.defaultValue) ? this.defaultValue : 0;
        // @ts-expect-error Check for typing
        let nextValue = toMaxPrecision(currentValue + delta, 0);
        nextValue = clamp(nextValue, this.min, this.max);
        this.value = nextValue;
        this.uxChange.emit();
    }
    handleButtonClick(direction) {
        const delta = this.getStepDelta(direction);
        this.incrementValue(delta);
    }
    getButtonEventHandlers(direction) {
        return {
            onMouseUp: () => this.clearStepTimer(),
            onMouseLeave: () => this.clearStepTimer(),
            onMouseDown: () => {
                this.handleButtonClick(direction);
                this.setStepTimer(direction);
            },
            onKeyDown: (e) => {
                if (isKeyboardClickKey(e.code)) {
                    this.handleButtonClick(direction);
                }
            }
        };
    }
    handleInputChange = (e) => {
        this.value = e.target.value;
    };
    render() {
        return (h(Host, { key: 'c4774feea185583b8835b534e8aee1e4c8166246', class: "ux-stepper" }, h("ux-input-field-number", { key: '2aded5ad604f301f6acf9c41cb91093cd016f657', class: "ux-stepper__input-field",
            // @ts-expect-error Check for typing
            ref: (el) => (this.inputFieldNumberElement = el), label: this.label, value: this.value, defaultValue: this.defaultValue, placeholder: this.placeholder, disabled: this.disabled, required: this.required, error: this.error, errorMsg: this.errorMsg, hintText: this.hintText, locale: this.locale, min: this.min, max: this.max, precision: this.precision, step: this.step, unit: this.unit, formatter: this.formatter, parser: this.parser, onUxChange: this.handleInputChange }, h("ux-button", { key: '47b4b33db1943530d9fa1ab55b392f54bb4e7891', slot: "buttons", tabindex: "-1", class: "ux-stepper__decrease", icon: "minus", size: ButtonSize.Icon, disabled: this.isStepDownDisabled, ...this.getButtonEventHandlers(Direction.DOWN) }), h("ux-button", { key: '55fad932452f9c0f4931a55c67e078e7c1a7769f', slot: "buttons", tabindex: "-1", class: "ux-stepper__increase", icon: "plus", size: ButtonSize.Icon, disabled: this.isStepUpDisabled, ...this.getButtonEventHandlers(Direction.UP) }))));
    }
    static get is() { return "ux-stepper"; }
    static get originalStyleUrls() {
        return {
            "$": ["stepper.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["stepper.css"]
        };
    }
    static get properties() {
        return {
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The name attribute of the input element"
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The placeholder text."
                },
                "attribute": "placeholder",
                "reflect": false
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The label."
                },
                "attribute": "label",
                "reflect": false
            },
            "defaultValue": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The default value of the input after initialization."
                },
                "attribute": "default-value",
                "reflect": false
            },
            "value": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A valid number or `Number.NaN`."
                },
                "attribute": "value",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to disable the input."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "required": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to mark the input as required."
                },
                "attribute": "required",
                "reflect": false,
                "defaultValue": "false"
            },
            "hintText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Information with low emphasis."
                },
                "attribute": "hint-text",
                "reflect": false
            },
            "error": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to `true`, the state is considered invalid."
                },
                "attribute": "error",
                "reflect": false,
                "defaultValue": "false"
            },
            "errorMsg": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Information to show if the value is invalid."
                },
                "attribute": "error-msg",
                "reflect": false
            },
            "locale": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The locale to use for input validation."
                },
                "attribute": "locale",
                "reflect": false
            },
            "min": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The minimum value."
                },
                "attribute": "min",
                "reflect": false
            },
            "max": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The maximum value."
                },
                "attribute": "max",
                "reflect": false
            },
            "step": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The increment size to use when using keyboard navigation."
                },
                "attribute": "step",
                "reflect": false,
                "defaultValue": "1"
            },
            "precision": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The maximum number of decimal places."
                },
                "attribute": "precision",
                "reflect": false,
                "defaultValue": "0"
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The unit of the value, e.g \"mm\", \"kg\"."
                },
                "attribute": "unit",
                "reflect": false
            },
            "formatter": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "FormatFn",
                    "resolved": "(value: string | number, isTyping: boolean) => string | null",
                    "references": {
                        "FormatFn": {
                            "location": "import",
                            "path": "../input-field-number/interface",
                            "id": "src/components/input-field-number/interface.ts::FormatFn"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A function that returns a string representation of a given number."
                }
            },
            "parser": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ParseFn",
                    "resolved": "(value: string) => number | undefined",
                    "references": {
                        "ParseFn": {
                            "location": "import",
                            "path": "../input-field-number/interface",
                            "id": "src/components/input-field-number/interface.ts::ParseFn"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A function that parses a number from a given string."
                }
            }
        };
    }
    static get events() {
        return [{
                "method": "uxChange",
                "name": "uxChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the value has changed."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "validate": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Performs a validity check on the input and sets `invalid` accordingly.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "domNode"; }
    static get watchers() {
        return [{
                "propName": "disabled",
                "methodName": "handleDisabledChanged"
            }];
    }
}
//# sourceMappingURL=stepper.js.map
